import { applySnapshot, getSnapshot, types } from "mobx-state-tree"
import { EmployeeList, Employer, initialState as emptyList } from "./employer-list"
import { initialState as emptyEmployer, EmployerType } from "./employer"
import { Transport } from "modules/common/models/transport"
import { Notificator } from "modules/common/models/notificator"
import { flow } from "modules/common/models/flow"
import { convertTextToImage, printImage, base64ToBlob, printPdf } from "modules/common/services/files"
import { getSiteAddress } from "modules/common/services/communication/http"
import { OwnSpendingsWidgetStore, initialState as emptyProjects } from "modules/main/models/own-spendings-store"
import { isNewlyCreated } from "modules/common/models/entity"
import { EmployerPremiums, initialState as emptyPremiums } from "./employer-premiums"
import { EmployeePositionDictionary } from "modules/spending/departments/models/employee-position-dictionary"
import { apiUrls } from "modules/common/services/communication/urls"
import { Constants } from "modules/root/models/constants"
import { buildAccessTree, makeColumns } from "modules/session/access/models/access-store"
import saveAs from "file-saver"
import { ProjectPortfolioDictionary, initialState as emptyPortfolios } from 'modules/dictionaries/project-portfolios/models/project-portfolio-dictionary'

interface EmployerDocumentNote {
  note: string
  count: number
}

export const EmployeeStore = types
  .compose(
    Transport,
    Notificator,
    types.model({
      positions: EmployeePositionDictionary,
      list: EmployeeList,
      removed: EmployeeList,
      details: Employer,
      premiums: EmployerPremiums,
      projects: OwnSpendingsWidgetStore,
      authorization: types.map(types.string),
      portfolios: ProjectPortfolioDictionary,
      /** примечания к документам, уже встречающиеся в базе (по убыванию частоты) */
      documentNotes: types.optional(types.array(types.string), []),
      /** счётчик незавершённых загрузок: список сотрудников приходит небыстро */
      loader: types.number,
    })
  )
  .actions((self) => ({
    loadSpendings(employerId: string) {
      if (isNewlyCreated(employerId)) {
        self.projects.clear()
        return Promise.resolve()
      }

      return self.projects.load(employerId, true)
    },
  }))
  .actions((self) => ({
    loadDocumentNotes: flow(function* () {
      try {
        const notes: EmployerDocumentNote[] = yield self.transport.get<any>(apiUrls.employee.documentNotes)

        applySnapshot(
          self.documentNotes,
          notes.map((item) => item.note)
        )
      } catch (er) {
        self.notify.error(er)
      }
    }),
  }))
  .actions((self) => ({
    init: flow(function* (employerId: string) {
      applySnapshot(self.authorization, getSnapshot(Constants.authorization))

      self.loader++

      try {
        yield Promise.all([
          self.details.load(employerId),
          self.premiums.load(employerId),
          self.loadSpendings(employerId),
          self.list.load(),
          self.removed.load(),
          self.positions.load(),
          self.portfolios.load(),
          self.loadDocumentNotes(),
        ])
      } finally {
        self.loader--
      }
    }),

    printCredentials: flow(function* (employer: EmployerType) {
      const credentials = yield employer.getCredentials()

      if (credentials) {



        const dataURL = convertTextToImage([
          `Логин : ${credentials.login}`,
          `Пароль: ${credentials.password}`,
          `Ссылка: ${getSiteAddress()}`,
        ])

        printImage(dataURL)
      }
    }),
    export: flow(function* () {
      try {
        let file: FileDescription
        file = yield self.transport.post(apiUrls.employee.exportWorkTime, { asPdf: false })
        const blob: any = yield base64ToBlob(file.content || "", file.mimeType)
        saveAs(blob, file.name)
      } catch (er) {
        self.notify.error(er)
        return false
      } finally {
      }
    }),
    printDocuments: flow(function* (notes: string[]) {
      if (!self.list.active.length || !notes.length) {
        return false
      }

      try {
        const ids = self.list.active.map((e) => e.id)

        const result: DownloadFileResult = yield self.transport.post<any>(apiUrls.employee.printAllDocuments, {
          ids,
          notes,
        })

        const blob: any = yield base64ToBlob(result.content || "", result.mimeType)

        if (blob) {
          const fileURL = URL.createObjectURL(blob)
          const printer = printPdf(fileURL, true)
          if (printer) {
            printer.onclose = () => URL.revokeObjectURL(fileURL)
          }
        }
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),
  }))
  .views((self) => ({
    get isLoading() {
      return self.loader > 0
    },

    /** примечания по алфавиту — для подсказок в карточке и выбора при массовой печати */
    get sortedDocumentNotes() {
      return Array.from(self.documentNotes).sort((a, b) => a.localeCompare(b, "ru"))
    },

    get accessTree() {
      return buildAccessTree(self.authorization.entries(), Array.from(self.authorization))
    },
  }))
  .views((self) => ({
    get accessColumns() {
      return makeColumns(self.accessTree, 1)
    },
  }))
  .named("EmployeeStore")

export const initialState = (): typeof EmployeeStore.SnapshotType => ({
  positions: { positions: [] },
  list: emptyList(),
  removed: emptyList(true),
  details: emptyEmployer(),
  premiums: emptyPremiums(),
  projects: emptyProjects(),
  authorization: {},
  portfolios: emptyPortfolios(),
  documentNotes: [],
  loader: 0,
})

export type EmployeeStoreType = typeof EmployeeStore.Type
